import { Stack, useRouter, useSegments } from 'expo-router';
import { Provider, useSelector } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { View, ActivityIndicator, Platform } from 'react-native';
import { useEffect, useState } from 'react';
import { useFonts } from 'expo-font';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import {
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
} from '@expo-google-fonts/inter';
import {
  PlayfairDisplay_400Regular,
  PlayfairDisplay_600SemiBold,
  PlayfairDisplay_700Bold,
} from '@expo-google-fonts/playfair-display';
import {
  Newsreader_400Regular,
  Newsreader_400Regular_Italic,
  Newsreader_700Bold,
  Newsreader_700Bold_Italic,
} from '@expo-google-fonts/newsreader';
import * as SplashScreen from 'expo-splash-screen';
import { store, persistor, RootState } from '../src/redux/store';
import { Colors } from '../src/styles/theme';
import '../src/i18n'; // Initialize i18n
import { I18nInitializer } from '../src/components/I18nInitializer';
import { VerdictPromptProvider } from '../src/components/household/VerdictPromptProvider';
import { NavigationTracker } from '../src/components/NavigationTracker';
import { quickValidation } from '../src/utils/firebaseValidator';
import { subscribeToAuthChanges, checkRedirectResult, waitForAuthReady, isOAuthRedirectPending, clearOAuthRedirectFlag } from '../src/services/authService';
import { startWebSessionFlagSync } from '../src/services/webSessionFlag';
import { setUser, loadHouseholdId } from '../src/redux/authSlice';
import { initAnalytics, identifyUser, resetUser, getAnalyticsContext, trackEvent, captureError, fontLoadTimeoutMs } from '../src/services/analyticsService';
import { buildBugReportState } from '../src/utils/bugReportState';
import { getLastReceiptScan } from '../src/services/receiptScanRecorder';
import { isNetworkAuthError } from '../src/utils/authErrors';
import { ErrorBoundary } from '../src/components/ErrorBoundary';
import { BugReporter } from '@youbbi/expo-bug-reporter';
import { getAuthHeaders } from '../src/services/apiClient';
import { selectOnboardingCompleted, selectOnboardingStartedAt, completeOnboarding } from '../src/redux/onboardingSlice';
import { markStartup, dumpStartupSummary, getStartupEventProps } from '../src/utils/startupPerf';

markStartup('layout module loaded');

// Keep the splash screen visible while we fetch fonts
SplashScreen.preventAutoHideAsync();

/**
 * AuthNavigator - Handles route guards based on authentication state
 *
 * This component must be rendered inside the Redux Provider.
 * It watches auth state and redirects users to appropriate routes:
 * - Authenticated users on /auth → redirect to /(app)
 * - Guest mode users can access /(app) without authentication
 */
function AuthNavigator() {
  const router = useRouter();
  const segments = useSegments();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const onboardingComplete = useSelector(selectOnboardingCompleted);
  const onboardingStartedAt = useSelector(selectOnboardingStartedAt);
  const hasMealPlan = useSelector((state: RootState) => {
    const weeks = state.mealPlan?.weeks;
    return !!(weeks && Object.values(weeks).some((meals: any) => meals && meals.length > 0));
  });

  useEffect(() => {
    const inAuthGroup = segments[0] === 'auth';
    const inAppGroup = segments[0] === '(app)';
    const inOnboarding = segments[0] === 'onboarding';
    const inJoinGroup = segments[0] === 'join';

    console.log('🔐 AuthNavigator - Route check:', {
      segments: segments.join('/'),
      inAuthGroup,
      inAppGroup,
      inOnboarding,
      isAuthenticated,
      onboardingComplete,
      user: user ? user.email : null,
    });

    // Auto-complete onboarding for existing users who already have data
    if (!onboardingComplete && (isAuthenticated && user || hasMealPlan)) {
      store.dispatch(completeOnboarding());
      return;
    }

    // New user: show onboarding (unless already there or on auth screen)
    if (!onboardingComplete && !inOnboarding && !inAuthGroup && !inJoinGroup) {
      // The marketing page comes first, on every host. Its CTAs link to
      // /?app=1, which is the only way into the quiz for a visitor who has
      // never started it; once started, a reload resumes the quiz instead.
      // window.location, not useLocalSearchParams: params are empty in a layout.
      if (Platform.OS === 'web' && !onboardingStartedAt && typeof window !== 'undefined'
          && !new URLSearchParams(window.location.search).has('app')) {
        console.log('🔐 AuthNavigator - Fresh visitor, showing the landing page');
        window.location.replace('/landing/');
        return;
      }
      console.log('🔐 AuthNavigator - New user, redirecting to onboarding');
      router.replace('/onboarding');
      return;
    }

    // NOTE: the /auth screen owns its own post-auth navigation. It shows a
    // success step after sign-in/registration and navigates to /(app) itself
    // (and bounces an already-authenticated visitor on arrival). We deliberately
    // do NOT force-redirect authenticated users off /auth here — doing so raced
    // the success screen and skipped it. Guests and unauthenticated users are
    // handled by the onboarding branch above.
  }, [isAuthenticated, user, segments, router, onboardingComplete, onboardingStartedAt, hasMealPlan]);

  return (
    <NavigationTracker>
      <Stack>
        <Stack.Screen name="(app)" options={{ headerShown: false }} />
        <Stack.Screen name="auth/index" options={{ headerShown: false }} />
        <Stack.Screen name="auth/reset" options={{ headerShown: false }} />
        <Stack.Screen name="onboarding" options={{ headerShown: false }} />
        <Stack.Screen name="share-partner" options={{ headerShown: false }} />
        <Stack.Screen name="join/[code]" options={{ headerShown: false }} />
      </Stack>
    </NavigationTracker>
  );
}

// Web: FontFaceObserver (used by expo-font) rejects with "6000ms timeout
// exceeded" on slow connections. The rejection escapes the library's own
// handling, popping the dev overlay and polluting error tracking — while the
// @font-face CSS stays injected and the font still swaps in when it arrives.
// Swallow that specific benign rejection; everything else propagates.
// PostHog's own listener still sees it: `rewriteOfflineExceptions` turns it
// into `font_load_timeout` there, with the same matcher.
if (Platform.OS === 'web' && typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const message = String((event.reason && event.reason.message) ?? event.reason ?? '');
    if (fontLoadTimeoutMs(message) != null) {
      console.warn('[fonts] webfont load timed out — continuing with system fonts');
      event.preventDefault();
    }
  });
}

export default function RootLayout() {
  const [fontsLoaded, fontError] = useFonts({
    'Inter-Regular': Inter_400Regular,
    'Inter-Medium': Inter_500Medium,
    'Inter-SemiBold': Inter_600SemiBold,
    'PlayfairDisplay-Regular': PlayfairDisplay_400Regular,
    'PlayfairDisplay-SemiBold': PlayfairDisplay_600SemiBold,
    'PlayfairDisplay-Bold': PlayfairDisplay_700Bold,
    'Newsreader-Regular': Newsreader_400Regular,
    'Newsreader-Italic': Newsreader_400Regular_Italic,
    'Newsreader-Bold': Newsreader_700Bold,
    'Newsreader-BoldItalic': Newsreader_700Bold_Italic,
  });

  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    // Validate Firebase setup on app startup (development only)
    if (__DEV__) {
      console.log('\n🔥 Firebase Validation Check:\n');
      quickValidation();
      console.log('\n');
    }

    // Initialize analytics (fire-and-forget, non-blocking)
    initAnalytics();

    let unsubscribe: (() => void) | null = null;

    // Leaves the breadcrumb the marketing page reads to decide whether this
    // visitor should see the story or the app. See webSessionFlag.ts.
    const unsubscribeSessionFlag = startWebSessionFlagSync();

    const initAuth = async () => {
      if (isOAuthRedirectPending()) {
        // Slow path: OAuth redirect in progress (iOS Safari Google sign-in).
        // Must process the redirect result BEFORE subscribing to onAuthStateChanged
        // to avoid the race where onAuthStateChanged fires null first.
        let gotRedirectUser = false;

        markStartup('auth init: checkRedirectResult start');
        try {
          const redirectResult = await checkRedirectResult();
          clearOAuthRedirectFlag();
          markStartup('auth init: checkRedirectResult done');
          if (redirectResult) {
            const redirectUser = redirectResult.user;
            console.log('✅ Redirect auth successful:', redirectUser.email);
            store.dispatch(setUser(redirectUser));
            store.dispatch(loadHouseholdId(redirectUser.uid));
            identifyUser(redirectUser.uid, { email: redirectUser.email, sign_up_method: 'google' });
            // This path (iOS Safari) never goes through loginWithGoogle.fulfilled,
            // so the auth event has to be emitted here or it is lost entirely.
            trackEvent(redirectResult.isNewUser ? 'user_signed_up' : 'user_logged_in', {
              method: 'google',
            });
            gotRedirectUser = true;
            markStartup('auth init: complete (redirect)');
            setAuthInitialized(true);
          }
        } catch (error) {
          // getRedirectResult resolves null when there was no redirect; a
          // rejection here is a Google sign-in that came back and failed
          // (iOS Safari takes this path), which used to be logged as "no
          // redirect result" and lost.
          console.error('[auth] redirect sign-in failed', error);
        }

        let firstCallbackFired = false;
        const authInitTimeout = setTimeout(() => {
          if (!firstCallbackFired) {
            firstCallbackFired = true;
            console.warn('⚠️ Auth init timeout — proceeding without Firebase response');
            markStartup('auth init: timeout fallback');
            setAuthInitialized(true);
          }
        }, 5000);

        unsubscribe = subscribeToAuthChanges((user) => {
          // iOS Safari race: onAuthStateChanged may fire null before Firebase syncs the redirect user
          if (gotRedirectUser && user === null) {
            console.log('🔄 Skipping null auth state (redirect user guard active)');
            gotRedirectUser = false;
            if (!firstCallbackFired) {
              firstCallbackFired = true;
              clearTimeout(authInitTimeout);
              markStartup('auth init: complete');
              setAuthInitialized(true);
            }
            return;
          }
          console.log('🔄 Firebase auth state changed:', user ? user.email : 'No user');
          store.dispatch(setUser(user));
          if (user) {
            const signUpMethod = user.isAnonymous ? 'guest' : user.photoURL ? 'google' : 'email';
            store.dispatch(loadHouseholdId(user.uid));
            identifyUser(user.uid, { email: user.email, sign_up_method: signUpMethod });
          } else {
            resetUser();
          }
          if (!firstCallbackFired) {
            firstCallbackFired = true;
            clearTimeout(authInitTimeout);
            markStartup('auth init: complete');
            setAuthInitialized(true);
          }
        });
      } else {
        // Fast path: read auth state from local cache (~50-200ms, no network call).
        // authStateReady() resolves as soon as Firebase restores the persisted session.
        markStartup('auth init: authStateReady start');
        let cachedUser = null;
        try {
          cachedUser = await waitForAuthReady();
        } catch (error) {
          console.warn('⚠️ authStateReady failed:', error);
        }
        markStartup('auth init: authStateReady done');

        store.dispatch(setUser(cachedUser));
        if (cachedUser) {
          const signUpMethod = cachedUser.isAnonymous ? 'guest' : cachedUser.photoURL ? 'google' : 'email';
          store.dispatch(loadHouseholdId(cachedUser.uid));
          identifyUser(cachedUser.uid, { email: cachedUser.email, sign_up_method: signUpMethod });
        }
        markStartup('auth init: complete');
        setAuthInitialized(true);

        // Set up ongoing listener in background — no longer blocks the initial render
        unsubscribe = subscribeToAuthChanges((user) => {
          console.log('🔄 Firebase auth state changed:', user ? user.email : 'No user');
          store.dispatch(setUser(user));
          if (user) {
            const signUpMethod = user.isAnonymous ? 'guest' : user.photoURL ? 'google' : 'email';
            store.dispatch(loadHouseholdId(user.uid));
            identifyUser(user.uid, { email: user.email, sign_up_method: signUpMethod });
          } else {
            resetUser();
          }
        });
      }
    };

    // Fire-and-forget, but never unhandled: anything rejecting outside initAuth's
    // own try blocks (the background subscribeToAuthChanges setup, Firebase's
    // proactive token refresh) used to escape as an unhandled rejection. Degrade
    // the way the fast path already does — no cached user, app still renders.
    initAuth().catch((error) => {
      if (isNetworkAuthError(error)) {
        console.warn('⚠️ Auth init failed (offline) — continuing without a cached user');
      } else {
        console.warn('⚠️ Auth init failed:', error);
        captureError(error instanceof Error ? error : new Error(String(error)), {
          context: 'auth_init',
        });
      }
      markStartup('auth init: failed fallback');
      setAuthInitialized(true);
    });

    return () => {
      console.log('🔄 Cleaning up Firebase auth state listener');
      if (unsubscribe) {
        unsubscribe();
      }
      unsubscribeSessionFlag();
    };
  }, []);

  // Fonts "ready" = loaded OR failed. On a slow connection the webfont load
  // can time out — the app must render with system-font fallbacks rather than
  // hang on the spinner (the font still swaps in later if it arrives).
  const fontsReady = fontsLoaded || fontError != null;

  useEffect(() => {
    if (fontsReady) {
      markStartup(fontError ? 'fonts failed — using system fallbacks' : 'fonts loaded');
      if (fontError) {
        console.warn('[fonts] loading failed, continuing with system fonts:', fontError.message);
      }
      SplashScreen.hideAsync();
    }
  }, [fontsReady, fontError]);

  useEffect(() => {
    if (fontsReady && authInitialized) {
      markStartup('render gate cleared (fonts + auth)');
      // Dump full summary shortly after first render so PersistGate timings land too
      const id = setTimeout(() => {
        dumpStartupSummary();
        trackEvent('app_startup_completed', getStartupEventProps());
      }, 2000);
      return () => clearTimeout(id);
    }
  }, [fontsReady, authInitialized]);

  // Wait for fonts (loaded or failed) AND auth to initialize before rendering
  if (!fontsReady || !authInitialized) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={Colors.primary} />
      </View>
    );
  }

  const appContent = (
    <Provider store={store}>
      <PersistGate
        loading={
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <ActivityIndicator size="large" color={Colors.primary} />
          </View>
        }
        persistor={persistor}
        onBeforeLift={() => markStartup('PersistGate: rehydrate complete')}
      >
        <I18nInitializer>
          {/* Mounted once, above the navigator: every cook gesture in the app
              opens the same household verdict sheet from here. */}
          <VerdictPromptProvider>
            <AuthNavigator />
          </VerdictPromptProvider>
        </I18nInitializer>
      </PersistGate>
    </Provider>
  );

  return (
    <ErrorBoundary>
      <GestureHandlerRootView style={{ flex: 1 }}>
        {__DEV__ ? (
          appContent
        ) : (
          <BugReporter
            webhookUrl="/api/bug-report"
            projectName="paprika"
            shakeOptions={{ multiTapCount: 5, multiTapWindowMs: 800 }}
            getHeaders={getAuthHeaders}
            getAppState={() => buildBugReportState(store.getState(), getAnalyticsContext(), getLastReceiptScan())}
          >
            {appContent}
          </BugReporter>
        )}
      </GestureHandlerRootView>
    </ErrorBoundary>
  );
}
